import { MainAppService } from "src/modules/app-template/services/main-app.service";
import { EnaioGuiField } from "src/modules/enaio/shared/EnaioGuiField";
import { EnaioWorkItemParameter } from "src/modules/enaio/shared/EnaioWorkItemParameter";
import { EnaioWorkflowBasicDataType } from "src/modules/enaio/shared/EnaioWorkflowBasicDataType";
import { PrimeMessageSeverity } from "src/modules/sm-base/models/prime-message-severity.model";
import { RestEndpoint } from "src/modules/sm-base/models/rest-endpoint.model";
import { FrontendFieldDefinition } from "src/modules/sm-base/models/frontend-field-definition.model";
import { FrontendFieldListItem } from "src/modules/sm-base/models/frontend-field-list-item.model";
import { OrdinaryObject } from "src/modules/utils/shared/ordinary-object.model";
import { Utils } from "src/modules/utils/shared/utils";
import { WorkflowForm } from "./workflow-form.model";

export class FormHelper {

    static instance: FormHelper;

    dms = new FormHelperDms();

    constructor (public app: MainAppService, public form: WorkflowForm) {
        FormHelper.instance = this;
    }

    addFile(_osid: number, _objectTypeId?: number, _fileArea?: string, _tryLocation?: number): void {
        throw new Error("Die Funktion addFile ist noch nicht implementiert");
    }

    getFields(): WfImplMaskField[] {
        return this.form.workItem.masks[0].fields.map(f => this.getFieldByName(f.name));
    }

    getFieldByName(name: string): WfImplMaskField {
        let field = this.form.workItem.masks[0].fields.find(f => f.name == name);
        return field != null ? new WfImplMaskField(field.guiField, this.form.form.getField(field.guiField.wfFieldId), this) : null;
    }

    getFieldByParameterName(name: string): WfImplMaskField {
        let param = this.form.workItem.parameters.find(p => p.name == name);
        if (param == null) {
            return null;
        }
        let field = this.form.workItem.masks[0].fields.find(f => param.formFieldId === f.guiField.wfFieldId);
        return field != null ? new WfImplMaskField(field.guiField, this.form.form.getField(field.guiField.wfFieldId), this) : null;
    }

    getFiles(): OrdinaryObject<WfImplTrayObject> {
        return Utils.arrayConvertToOrdinaryObject(this.form.workItem.files, file => Utils.toString(file.id), _ => new WfImplTrayObject());
    }

    getInfo(): OrdinaryObject {
        return {
            user: {
                name: this.app.getUserName(),
                fullName: this.form.enaioUserName
            },
            workflow: {
                name: this.form.workItem.activityName,
                activityId: this.form.workItem.activityId,
                workflowId: this.form.workItem.workflowId,
                processId: this.form.workItem.processId,
                workflowType: 1,
                rActivityId: this.form.workItem.id
            }
        };
    }

    getParameters(): WfImplParam[] {
        return this.form.workItem.parameters.map(p => this.getParameterByName(p.name));
    }

    getParameterByName(name: string): WfImplParam {
        let param = this.form.workItem.parameters.find(p => p.name == name);
        return param != null ? new WfImplParam(param, this.getFieldByParameterName(param.name)) : null;
    }

    async includePublicScript(url: string): Promise<void> {
        (window as any).clientScriptFunctions = this.form.clientScriptFunctions;
        let id = "clientScriptPublic_0_";

        let code = await RestEndpoint.main().query({url}).run("api/wfl/formHelper/includePublicScript").getString();
        code = "window.clientScriptFunctions['" + id + "'] = (function anonymous(formHelper,globals,scriptingStorage) {\nadditionParameter = [window.formHelper];" + code + "\n})";
        this.form.loadScript(id, code);

        delete (window as any).clientScriptFunctions;

        await this.form.runScriptByName("clientScriptPublic_0_");
    }

    showToast(severity: PrimeMessageSeverity, detail: string, summary: string, duration?: number): void {
        this.app.showToast(severity, summary, detail, duration);
    }
}

export class FormHelperDms {

    async search(query: string): Promise<OrdinaryObject[]> {
        return Utils.fromJson(await RestEndpoint.main().query({query: Utils.toJson(query)}).run("api/wfl/formHelper/dmsSearch").getString()) as OrdinaryObject[];
    }
}

export class WfImplTrayObject {
    
}

export class WfImplMaskField {

    api: WfImplMaskFieldApi;
    model: WfImplMaskFieldModel;
    value: any;

    constructor(public field: EnaioGuiField, public guiField: FrontendFieldDefinition, public formHelper: FormHelper) {
        this.api = new WfImplMaskFieldApi(this, field, guiField, formHelper);
        this.model = new WfImplMaskFieldModel(this, field, guiField);
        this.value = this.api.getValue();
    }
}

export class WfImplMaskFieldApi {

    constructor (public internal: WfImplMaskField, public field: EnaioGuiField, public guiField: FrontendFieldDefinition, public formHelper: FormHelper) {
    }

    disable(): void {
        this.guiField.enabled = false;
    }

    enable(): void {
        this.guiField.enabled = true;
    }

    getValue(): any {
        return this.guiField.value;
    }

    hide(): void {
        this.guiField.visible = false;
    }

    setCustomListEntries(items: any[]): void {
        this.guiField.listItems = items.map(item => {
            let value = item.value;
            if (Utils.isNoe(value)) {
                value = item.short;
            }
            if (value == null) {
                value = item;
            }
            return new FrontendFieldListItem(Utils.hasProperty(item, "short") ? item.short : item, Utils.toString(value));
    });
    }

    setInvalid(title?: string, message?: string): void {
        this.guiField.errorMessage = Utils.stringDef(message, FormHelper.instance.app.t("general.invalidInput"));
        this.guiField.errorMessageFromScript = this.guiField.errorMessage;
    }

    getRequired(initial: boolean): boolean {
        if (initial) {
            throw new Error("getRequired(true) wird noch nicht unterstützt");
        }
        return this.guiField.mandatory;
    }

    setRequired(value: boolean): void {
        this.guiField.mandatory = value;
    }

    setTitle(title: string): void {
        this.guiField.caption = title;
    }

    setValid(): void {
        this.guiField.errorMessage = null;
        this.guiField.errorMessageFromScript = null;
        this.guiField.tableMustRefresh = true;
    }

    setValue(value: any): void {
        this.guiField.setValue(value);
        this.guiField.onValueChanged(false);
        this.guiField.tableMustRefresh = true;
    }

    show(): void {
        this.guiField.visible = true;
    }

    getRows(): any[] {
        return this.guiField.value ?? [];
    }

    addRow(values?: any[], rowIndex?: number): void {
        let newRow = this.guiField.generateEmptyTableItem();
        for (let col of this.guiField.tableColumns.entries()) {
            if (values?.length > col[0]) {
                newRow[col[1].id] = values[col[0]];
            }
        }
        if (rowIndex == null) {
            this.guiField.value = [...this.guiField.value, newRow];
        }
        else {
            this.guiField.value = (this.guiField.value as any[]).splice(rowIndex, 0, [newRow]);
        }
    }

    getCellValue(row: number, column: number): any {
        let result = this.guiField.value[row][this.guiField.tableColumns[column].id];
        if (result instanceof Date) {
            result = Utils.dateFormatDefaultDate(result);
        }
        return result;
    }

    isCellEnabled(row: number, column: number): boolean {
        return this.guiField.isTableCellEnabled(row, column);
    }

    setCellValue(row: number, column: number, value: any): void {
       this.guiField.value[row][this.guiField.tableColumns[column].id] = value;
       this.guiField.onValueChanged(false);
       this.guiField.tableMustRefresh = true;
    }

    disableCell(row: number, column: number): void {
        this.guiField.tableDisabledCells ??= [];
        let value = {row, column};
        if (!this.guiField.tableDisabledCells.some(cell => cell.row == row && cell.column == column)) {
            this.guiField.tableDisabledCells = [...this.guiField.tableDisabledCells, value];
        }
        this.guiField.tableMustRefresh = true;
    }

    enableCell(row: number, column: number): void {
        if (this.guiField.tableDisabledCells == null) {
            return;
        }
        let value = {row, column};
        let index = this.guiField.tableDisabledCells.findIndex(cell => cell.row == row && cell.column == column);
        if (index >= 0) {
            this.guiField.tableDisabledCells.splice(index, 1);
        }
        this.guiField.tableMustRefresh = true;
    }

    setCellValid(row: number, column: number): void {
        this.guiField.setCellValid(row, column, true);
        this.guiField.tableMustRefresh = true;
    }

    setCellInvalid(row: number, column: number, errorMessage: string): void {
        this.guiField.setCellValid(row, column, false, errorMessage);
        this.guiField.tableMustRefresh = true;
    }

    deselectRows(rows: number[]) {
        if (rows.some(row => row == this.guiField.tableSelectedRow)) {
            this.guiField.tableSelectedRowRequest = -1;
        }
    }

    selectRows(rows: number[]) {
        if (rows.length > 0) {
            this.guiField.tableSelectedRowRequest = rows[0];
        }
    }

    getSelectedRows(): number[] {
        return this.guiField.tableSelectedRow >= 0 ? [this.guiField.tableSelectedRow] : [];
    }

    goToPage(pageName: string): void {
        this.guiField.pageSelected = this.guiField.children.findIndex(page => page.caption == pageName);
    }

    focus(): void {
        this.guiField.requestFocus = true;
    }

    // Eigene Methoden, die nicht vom enaio WebClient unterstützt werden

    setTableCanAddRow(value: boolean): void {
        this.guiField.tableCanAddRow = value;
    }

    setTableCanDeleteRow(value: boolean): void {
        this.guiField.tableCanRemoveRow = value;
    }

    setTableColumnNumDecimalDigits(columnIndex: number, value: number): void {
        this.guiField.tableColumns[columnIndex].numDecimalDigits = value;
    }

    setTableColumnExpandDecimalDigits(columnIndex: number, value: boolean): void {
        this.guiField.tableColumns[columnIndex].expandDecimalDigits = value;
    }

    setTableColumnToolTip(columnIndex: number, value: string): void {
        this.guiField.tableColumns[columnIndex].toolTip = value;
    }

    setDropdownCanFilter(value: boolean): void {
        this.guiField.dropdownCanFilter = value;
    }

}

export class WfImplMaskFieldModel {

    internal: string;
    name: string;

    constructor (public impl: WfImplMaskField, public field: EnaioGuiField, public guiField: FrontendFieldDefinition) {
        this.internal = field.internalName;
        this.name = field.name;
    }
}

export class WfImplParam {

    api: WfImplParamApi;
    model: WfImplParamModel;
    value: any;

    constructor(public field: EnaioWorkItemParameter, public maskField: WfImplMaskField) {
        this.api = new WfImplParamApi(this, field, maskField);
        this.model = new WfImplParamModel(this, field, maskField);
        this.value = this.api.getValue();
    }
}

export class WfImplParamApi {
    constructor (public internal: WfImplParam, public field: EnaioWorkItemParameter, public maskField: WfImplMaskField) {
    }

    getValue(): any {
        if ((this.field.value?.dataType?.basic == EnaioWorkflowBasicDataType.date || this.field.value?.dataType?.basic == EnaioWorkflowBasicDataType.dateTime) && this.field.value?.value == null) {
            return "";
        }
        else if (this.field.value?.value instanceof Date) {
            return Utils.dateFormatDefaultDate(this.field.value.value);
        }
        else if (this.field.value?.dataType?.basic == EnaioWorkflowBasicDataType.record) {
            return { record: this.field.value?.value };
        }
        else {
            let value = this.field.value?.value;
            return Utils.isNumber(value) ? Utils.toString(value) : value;
        }
    }

    setValue(value: any): void {
        this.field.value.value = value;
        if (this.maskField != null) {
            this.maskField.api.setValue(value);
        }
    }
}

export class WfImplParamModel {

    name = "";

    constructor (public internal: WfImplParam, public field: EnaioWorkItemParameter, public maskField: WfImplMaskField) {
        this.name = field.name;
    }
}
